import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GridApi, GridReadyEvent, RowDragEvent } from 'ag-grid-community'
import { ProgressReportByTeamPageConfig } from '../../../../../domain/entity/version-report/VersionReportTemplateEntity'
import {
  ProgressReportByTeamPageContent,
  ProgressReportQualitativeContent,
} from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  ProgressReportTable,
  ProgressReportTableContent,
} from '../table/ProgressReportTable'
import { DeliverableTaskPopper } from '../dialog/DeliverableTaskPopper'
import {
  ProgressValue,
  WbsItemAggregateCategory,
} from '../../../../../domain/value-object/ProjectPlanSnapshot'
import { useProjectPlanSnapshotRepository } from '../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import { aggregatedValueService } from '../../../../../domain/value-object/AggregatedValueVO'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../domain/entity/WbsItemEntity'
import { EditConfigButton } from '.'
import { ProgressReportByTeamConfigDialog } from '../dialog/ProgressReportByTeamConfigDialog'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useTeamReportColumnDef } from '../../hooks/columnDef'
import { useProgressReportDragAndDrop } from '../../hooks/dragAndDrop'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { useDiscussionActionContext } from '../../context/DiscussionContext'
import { TargetCandidate } from '../../../../../domain/entity/DiscussionEntity'

type ProgressReportByTeamProps = {
  snapshotUuid: string
  editing: boolean
  config: ProgressReportByTeamPageConfig
  content: ProgressReportByTeamPageContent
  updatePageConfig: <
    K extends keyof ProgressReportByTeamPageConfig,
    V extends ProgressReportByTeamPageConfig[K],
  >(
    path: K,
    value: V
  ) => void
  updatePageContent: <
    K extends keyof ProgressReportByTeamPageContent,
    V extends ProgressReportByTeamPageContent[K],
  >(
    path: K,
    value: V
  ) => void
}
export const ProgressReportByTeam = ({
  editing,
  snapshotUuid,
  config,
  content,
  updatePageConfig,
  updatePageContent,
}: ProgressReportByTeamProps) => {
  const [progressValues, setProgressValues] = useState<ProgressValue[]>([])
  const { pivotByTeamUuids } = useProjectPlanSnapshotRepository()
  const { setTargetCandidates } = useDiscussionActionContext()

  useEffect(() => {
    const candidates: TargetCandidate[] = progressValues.map(v => ({
      uuid: v.uuid,
      name: v.name,
      type: 'TEAM',
    }))
    setTargetCandidates(candidates)
    return () => {
      setTargetCandidates([])
    }
  }, [progressValues, setTargetCandidates])

  const gridApiRef = useRef<GridApi | null>(null)
  const [isGridReady, setIsGridReady] = useState(false)
  const saveTableRows = useCallback(
    (uuids: string[]) => updatePageConfig('tableRows', uuids),
    [updatePageConfig]
  )
  const {
    contextRef,
    onRowDragEnter,
    onRowDragMove,
    onRowDragEnd,
    onRowDragLeave,
    skipNextFetchRef,
  } = useProgressReportDragAndDrop({
    gridApi: isGridReady ? gridApiRef.current || undefined : undefined,
    progressValues,
    setProgressValues,
    saveTableRows,
  })
  const onGridReady = useCallback((e: GridReadyEvent) => {
    gridApiRef.current = e.api
    setIsGridReady(true)
  }, [])
  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      return
    }
    const fn = async () => {
      const response = await pivotByTeamUuids(
        snapshotUuid,
        config.rootUuid,
        config.tableRows
      )
      setProgressValues(response)
    }
    fn()
  }, [
    config.rootUuid,
    config.tableRows,
    pivotByTeamUuids,
    snapshotUuid,
    skipNextFetchRef,
  ])
  const tableContents = useMemo(
    () =>
      progressValues.map(v => {
        const qualitativeContent = content.tableContents.find(
          u => u.uuid === v.uuid
        )
        return {
          uuid: v.uuid,
          name: v.name,
          status: qualitativeContent?.status || 'SUNNY',
          comment: qualitativeContent?.comment || '',
          scheduledProgressRate:
            aggregatedValueService.getFormattedRatePercentage(
              v.planned,
              v.total,
              WbsItemType.TASK,
              AggregateField.WBS_ITEM_COUNT
            ),
          progressRate: aggregatedValueService.getFormattedRatePercentage(
            v.completed,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          total: aggregatedValueService.getFormatted(
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          planned: aggregatedValueService.getFormatted(
            v.planned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          completed: aggregatedValueService.getFormatted(
            v.completed,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          delay: aggregatedValueService.getFormatted(
            v.delay,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          remaining: aggregatedValueService.getFormatted(
            v.remaining,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          wbsItemStatus: v.wbsItemStatus,
          team: v.team,
          progressRateDiff: aggregatedValueService.getFormattedRatePercentage(
            v.plannedCompletedDiff,
            v.total,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          scheduledStartDate: dateV2Service
            .fromString(v.scheduledDate?.startDate)
            ?.toDateString(),
          scheduledEndDate: dateV2Service
            .fromString(v.scheduledDate?.endDate)
            ?.toDateString(),
          maxChildScheduledEndDate: v.maxChildScheduledEndDate,
          preceding: aggregatedValueService.getFormatted(
            v.preceding,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
          unplanned: aggregatedValueService.getFormatted(
            v.unplanned,
            WbsItemType.TASK,
            AggregateField.WBS_ITEM_COUNT
          ),
        }
      }),
    [content.tableContents, progressValues]
  )
  const qualitativeContents: ProgressReportQualitativeContent[] = useMemo(
    () =>
      tableContents.map(v => ({
        uuid: v.uuid,
        status: v.status,
        comment: v.comment,
      })),
    [tableContents]
  )
  const updateTableContent = useCallback(
    (v: ProgressReportQualitativeContent, index: number) => {
      const newContent = qualitativeContents.map((u, i) => {
        if (i !== index) return u
        return v
      })
      updatePageContent('tableContents', newContent)
    },
    [qualitativeContents, updatePageContent]
  )
  const updateConfig = useCallback(
    (rootUuid: string | undefined, teamUuids: string[]) => {
      updatePageConfig('tableRows', teamUuids)
      updatePageConfig('rootUuid', rootUuid)
    },
    [updatePageConfig]
  )
  const [open, openDialog, closeDialog] = useDialogState()

  const [popperState, setPopperState] = useState<{
    anchorEl: HTMLElement
    clickPosition: { x: number; y: number }
    teamUuid: string
    aggregateType: WbsItemAggregateCategory
  } | null>(null)
  const closePopper = useCallback(() => setPopperState(null), [])
  const handleNumericCellClick = useCallback(
    (
      data: ProgressReportTableContent,
      anchorEl: HTMLElement,
      clickPosition: { x: number; y: number },
      aggregateType: WbsItemAggregateCategory
    ) => {
      if (!config.rootUuid) return
      setPopperState({
        anchorEl,
        clickPosition,
        teamUuid: data.uuid,
        aggregateType,
      })
    },
    [config.rootUuid]
  )
  const handleRowDragEnter = useCallback(
    (event: RowDragEvent) => {
      setPopperState(null)
      onRowDragEnter(event)
    },
    [onRowDragEnter]
  )

  useEffect(() => {
    if (editing) setPopperState(null)
  }, [editing])

  const columns = useTeamReportColumnDef({
    editing,
    updateTableContent,
    onNumericCellClick: handleNumericCellClick,
  })
  return (
    <>
      <ProgressReportTable
        columns={columns}
        editing={editing}
        contents={tableContents}
        context={contextRef.current}
        onGridReady={onGridReady}
        onRowDragEnter={handleRowDragEnter}
        onRowDragMove={onRowDragMove}
        onRowDragLeave={onRowDragLeave}
        onRowDragEnd={onRowDragEnd}
      />
      <DeliverableTaskPopper
        anchorEl={popperState?.anchorEl ?? null}
        clickPosition={popperState?.clickPosition}
        rootUuid={config.rootUuid}
        teamUuid={popperState?.teamUuid}
        aggregateType={popperState?.aggregateType}
        aggregateTarget={WbsItemType.TASK}
        snapshotUuid={snapshotUuid}
        onClose={closePopper}
      />
      <EditConfigButton editing={editing} onClick={openDialog} />
      <ProgressReportByTeamConfigDialog
        open={open}
        onClose={closeDialog}
        updateConfig={updateConfig}
        config={config}
        snapshotUuid={snapshotUuid}
      />
    </>
  )
}
